import { useEffect, useId, useState } from 'react';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Checkbox, FormField, Input } from '@ds/forms';
import { diaIso, moeda } from '@/comuns/formato';
import { quantidade, unidadeLabel } from '@/comuns/unidades';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { PedidoDeCompra } from '@/data/tipos';

export interface RecebimentoModalProps {
    pedido: PedidoDeCompra | null;
    onClose: () => void;
    onRecebido: () => void;
}

/**
 * O que chegou.
 *
 * Vem preenchido com o que falta de cada item — receber tudo é o caso comum, e
 * digitar de novo é onde nasce o erro. O que a pessoa muda é a exceção: o item
 * que veio pela metade ou não veio.
 */
export function RecebimentoModal({ pedido, onClose, onRecebido }: RecebimentoModalProps) {
    const aviso = useAviso();
    const idData = useId();
    const [chegou, setChegou] = useState<Record<string, string>>({});
    const [dia, setDia] = useState(diaIso());
    const [lancar, setLancar] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!pedido) return;
        setErro(null);
        setDia(diaIso());
        setLancar(true);
        setChegou(Object.fromEntries(pedido.itens.map((i) => [i.id, String(i.quantidade - i.quantidadeRecebida)])));
    }, [pedido]);

    if (!pedido) return null;

    const valorDe = (itemId: string) => {
        const item = pedido.itens.find((i) => i.id === itemId);
        if (!item) return 0;
        return (Number((chegou[itemId] ?? '0').replace(',', '.')) || 0) * item.custoUnitarioCentavos;
    };
    const total = pedido.itens.reduce((s, i) => s + valorDe(i.id), 0);

    const receber = async () => {
        setSalvando(true);
        try {
            await dados().receberPedido({
                pedidoId: pedido.id,
                itens: pedido.itens.map((i) => ({ itemId: i.id, quantidade: Number((chegou[i.id] ?? '0').replace(',', '.')) || 0 })),
                recebidoEm: dia,
                lancarContaAPagar: lancar,
            });
            aviso({
                title: 'Mercadoria recebida',
                description: `${pedido.numero} · entrada no estoque${lancar ? ' e conta a pagar lançada' : ''}`,
            });
            onRecebido();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível receber.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={600}
            title={`Receber ${pedido.numero}`}
            description={`${pedido.fornecedor}${pedido.observacoes ? ` · ${pedido.observacoes}` : ''}`}
            dirty={total > 0}
            onSave={() => void receber()}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void receber()} loading={salvando} disabled={total <= 0}>Registrar entrada</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {pedido.itens.map((item) => {
                        const falta = item.quantidade - item.quantidadeRecebida;
                        return (
                            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{item.produto}</span>
                                    <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                        {item.quantidadeRecebida > 0
                                            ? `${quantidade(item.quantidadeRecebida)} de ${quantidade(item.quantidade)} já recebidos · faltam ${quantidade(falta)} ${unidadeLabel(falta, item.unidade)}`
                                            : `${quantidade(item.quantidade)} ${unidadeLabel(item.quantidade, item.unidade)} · ${moeda(item.custoUnitarioCentavos)} cada`}
                                    </span>
                                </span>
                                <span style={{ width: 92, flex: '0 0 auto' }}>
                                    <Input
                                        value={chegou[item.id] ?? '0'} aria-label={`Chegou de ${item.produto}`}
                                        disabled={falta <= 0}
                                        onChange={(v) => { setChegou((atual) => ({ ...atual, [item.id]: v })); setErro(null); }}
                                    />
                                </span>
                                <span style={{ width: 110, flex: '0 0 auto', textAlign: 'right', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                    {moeda(valorDe(item.id))}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>Total recebido</span>
                    <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{moeda(total)}</span>
                </div>

                <FormField label="Data da entrada" htmlFor={idData}>
                    <Input id={idData} type="date" value={dia} onChange={setDia} />
                </FormField>

                {/* À vista e à escolha: título criado por baixo do pano é o que
                    ninguém encontra depois, e recebimento parcial não deve
                    lançar a nota inteira. */}
                <Checkbox
                    checked={lancar} onChange={setLancar}
                    label="Lançar conta a pagar no Financeiro"
                    description={`${moeda(total)} em "Produtos e insumos", vencendo na data da entrada`}
                />

                <Alert tone="neutral" dismissible={false} title="O que isto faz no estoque">
                    Cada item vira uma entrada em Estoque › Movimentações, com o pedido gravado na linha. O custo do
                    cadastro passa a ser o desta compra — é ele que alimenta o custo dos procedimentos.
                </Alert>
            </div>
        </Modal>
    );
}
